'use client'

import { cn } from '@/lib/utils'

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showIcon?: boolean
  showText?: boolean
  className?: string
}

function NeonMusicNote({ size }: { size: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 502 559"
      width={size}
      height={size}
      fill="none"
      className="neon-logo"
      style={{ color: '#ff4fa3' }}
    >
      <path
        d="M492.0,5.0 L486.0,5.0 L483.0,0.0 L468.0,0.0 L460.0,5.0 L447.0,6.0 L412.0,21.0 L402.0,21.0 L387.0,29.0 L269.0,65.0 L267.0,69.0 L256.0,69.0 L242.0,77.0 L209.0,85.0 L204.0,93.0 L200.0,93.0 L196.0,120.0 L198.0,373.0 L181.0,373.0 L150.0,364.0 L112.0,365.0 L82.0,374.0 L61.0,385.0 L31.0,408.0 L9.0,438.0 L0.0,469.0 L6.0,509.0 L22.0,532.0 L28.0,532.0 L32.0,540.0 L68.0,556.0 L104.0,559.0 L132.0,556.0 L172.0,539.0 L208.0,508.0 L228.0,474.0 L236.0,434.0 L234.0,195.0 L265.0,180.0 L280.0,178.0 L288.0,172.0 L297.0,172.0 L333.0,156.0 L386.0,140.0 L398.0,132.0 L432.0,123.0 L444.0,116.0 L445.0,94.0 L437.0,93.0 L423.0,101.0 L413.0,101.0 L403.0,108.0 L381.0,113.0 L379.0,117.0 L367.0,117.0 L356.0,124.0 L335.0,128.0 L308.0,141.0 L298.0,141.0 L244.0,162.0 L234.0,161.0 L233.0,118.0 L252.0,108.0 L268.0,106.0 L276.0,100.0 L294.0,97.0 L297.0,92.0 L314.0,90.0 L318.0,85.0 L343.0,80.0 L367.0,68.0 L378.0,68.0 L390.0,60.0 L401.0,60.0 L439.0,44.0 L461.0,43.0 L463.0,320.0 L400.0,317.0 L370.0,325.0 L338.0,341.0 L302.0,373.0 L301.0,381.0 L294.0,386.0 L293.0,395.0 L288.0,400.0 L285.0,427.0 L293.0,454.0 L303.0,468.0 L308.0,468.0 L309.0,473.0 L324.0,484.0 L332.0,484.0 L333.0,489.0 L356.0,492.0 L362.0,497.0 L396.0,497.0 L421.0,491.0 L455.0,473.0 L481.0,448.0 L494.0,427.0 L502.0,395.0 L500.0,24.0 Z M198.0,422.0 L197.0,458.0 L189.0,468.0 L189.0,476.0 L182.0,477.0 L180.0,485.0 L175.0,485.0 L172.0,492.0 L166.0,493.0 L148.0,509.0 L141.0,509.0 L129.0,516.0 L103.0,520.0 L69.0,517.0 L53.0,509.0 L44.0,500.0 L38.0,488.0 L39.0,462.0 L44.0,451.0 L53.0,439.0 L60.0,435.0 L62.0,428.0 L68.0,428.0 L74.0,421.0 L85.0,417.0 L88.0,412.0 L97.0,412.0 L109.0,408.0 L110.0,405.0 L166.0,405.0 L167.0,408.0 L178.0,412.0 L188.0,412.0 L189.0,417.0 Z M463.0,366.0 L464.0,392.0 L460.0,414.0 L441.0,435.0 L430.0,440.0 L428.0,444.0 L413.0,449.0 L411.0,453.0 L360.0,456.0 L341.0,451.0 L324.0,436.0 L324.0,408.0 L346.0,381.0 L370.0,365.0 L395.0,357.0 L422.0,356.0 L436.0,357.0 Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  )
}

export function Logo({ size = 'md', showIcon = true, showText = true, className }: LogoProps) {
  const sizeClasses = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-2xl',
    xl: 'text-4xl',
  }

  const iconSizes = {
    sm: 18,
    md: 24,
    lg: 32,
    xl: 44,
  }

  return (
    <div className={cn('flex items-center gap-4', className)}>
      {showIcon && (
        <NeonMusicNote size={iconSizes[size]} />
      )}
      {showText && (
        <span className={cn('font-bold', sizeClasses[size])}>
          <span className="text-white">iPerform.</span>
          <span className="neon-text">live</span>
        </span>
      )}
      
      <style jsx global>{`
        .neon-logo {
          filter:
            drop-shadow(0 0 6px #ff4fa3)
            drop-shadow(0 0 14px #ff4fa3)
            drop-shadow(0 0 28px rgba(255, 79, 163, 0.85));
        }
        
        .neon-text {
          color: #ff4d8a;
          text-shadow:
            0 0 4px rgba(255, 77, 138, 0.3),
            0 0 8px rgba(255, 77, 138, 0.15);
          animation: neon-pulse-logo 2s ease-in-out infinite alternate;
        }

        @keyframes neon-pulse-logo {
          from {
            text-shadow:
              0 0 4px rgba(255, 77, 138, 0.3),
              0 0 8px rgba(255, 77, 138, 0.15);
          }
          to {
            text-shadow:
              0 0 2px rgba(255, 77, 138, 0.2),
              0 0 4px rgba(255, 77, 138, 0.1);
          }
        }
      `}</style>
    </div>
  )
}

// Icon-only logo component for use in compact spaces
export function LogoIcon({ 
  size = 'md',
  className 
}: { 
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string 
}) {
  const iconSizes = {
    sm: 18,
    md: 24,
    lg: 32,
    xl: 44,
  }

  return (
    <div className={cn('flex-shrink-0', className)}>
      <NeonMusicNote size={iconSizes[size]} />
    </div>
  )
}

// Standalone neon text component for use in other places
export function NeonText({ 
  children, 
  className,
  color = 'pink' 
}: { 
  children: React.ReactNode
  className?: string
  color?: 'pink' | 'cyan' | 'green' | 'orange' | 'purple'
}) {
  const colors = {
    pink: {
      color: '#ff1867',
      glow: 'rgba(255, 24, 103, 0.5)',
    },
    cyan: {
      color: '#00f5ff',
      glow: 'rgba(0, 245, 255, 0.5)',
    },
    green: {
      color: '#39ff14',
      glow: 'rgba(57, 255, 20, 0.5)',
    },
    orange: {
      color: '#ff6b35',
      glow: 'rgba(255, 107, 53, 0.5)',
    },
    purple: {
      color: '#bf00ff',
      glow: 'rgba(191, 0, 255, 0.5)',
    },
  }

  const c = colors[color]

  return (
    <>
      <span className={cn('neon-text-custom', className)}>{children}</span>
      <style jsx>{`
        .neon-text-custom {
          color: ${c.color};
          text-shadow:
            0 0 5px ${c.color},
            0 0 10px ${c.color},
            0 0 20px ${c.color},
            0 0 40px ${c.color},
            0 0 80px ${c.glow};
          animation: neon-pulse-custom 2s ease-in-out infinite alternate;
        }
        
        @keyframes neon-pulse-custom {
          from {
            text-shadow:
              0 0 5px ${c.color},
              0 0 10px ${c.color},
              0 0 20px ${c.color},
              0 0 40px ${c.color},
              0 0 80px ${c.glow};
          }
          to {
            text-shadow:
              0 0 2px ${c.color},
              0 0 5px ${c.color},
              0 0 10px ${c.color},
              0 0 20px ${c.color},
              0 0 40px ${c.glow.replace('0.5', '0.3')};
          }
        }
      `}</style>
    </>
  )
}

